import { useEffect, useMemo, useRef, useState, useId, type KeyboardEvent, type ReactNode } from 'react';
import type { TagSuggestion } from '../../types/videos';

interface TagEditorProps {
  /** Manual tags — the editable set; add/remove here is the manual tagging flow. */
  tags: string[];
  onChange: (tags: string[]) => void;
  /** Tag vocabulary offered in the input dropdown. */
  suggestions?: string[];
  /** Confirmed LLM tags (source = accepted_suggestion) — real tags, provenance shown. */
  acceptedTags?: string[];
  /** Pending LLM proposals awaiting an accept/dismiss decision. */
  pendingSuggestions?: TagSuggestion[];
  onDecideSuggestion?: (suggestion: TagSuggestion, decision: 'accept' | 'reject') => void;
}

function normalizeTag(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

/** Inline checkmark — the one-click accept affordance inside a suggestion chip. */
function TickIcon() {
  return (
    <svg
      viewBox="0 0 12 12"
      className="w-3 h-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 6.5 4.8 9 10 3.5" />
    </svg>
  );
}

/** Inline minus — the non-colour removal cue on red removal proposals. */
function MinusIcon() {
  return (
    <svg
      viewBox="0 0 12 12"
      className="w-3 h-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M2.5 6h7" />
    </svg>
  );
}

/**
 * Small text pill marking chip state — the tag-chip analogue of the
 * SourcePill provenance pattern used in CaptionPanel.
 */
function ChipPill({ children, title, className = '' }: { children: ReactNode; title?: string; className?: string }) {
  return (
    <span
      title={title}
      className={`text-[10px] leading-none px-1.5 py-0.5 rounded-full font-medium ${className}`}
    >
      {children}
    </span>
  );
}

/** A confirmed LLM tag: as valid as a manual tag, kept visually distinguishable. */
function AcceptedTagChip({ tag }: { tag: string }) {
  return (
    <span
      title="Accepted from an AI tag suggestion"
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-violet-50 text-violet-800 border border-violet-200"
    >
      {tag}
      <ChipPill title="Added by the AI tag engine, confirmed by you" className="bg-violet-100 text-violet-700">
        ai
      </ChipPill>
    </span>
  );
}

/**
 * A pending LLM proposal — either an addition (dashed blue) or a removal
 * (red + strikethrough + minus icon, so the meaning never relies on colour
 * alone). The tick confirms in one click; the cross dismisses and records a
 * server-side rejection. The model's rationale is on the chip title.
 */
function SuggestionChip({
  suggestion,
  onDecide,
}: {
  suggestion: TagSuggestion;
  onDecide?: TagEditorProps['onDecideSuggestion'];
}) {
  const isAdd = suggestion.action === 'add';
  const rationale = suggestion.rationale?.trim();
  const title = isAdd
    ? `AI suggests adding this tag${
        suggestion.is_new_tag ? ' — new vocabulary, not in the current tag inventory' : ''
      }${rationale ? `: ${rationale}` : ''}`
    : `AI suggests removing this tag${rationale ? `: ${rationale}` : ''}`;

  const tone = isAdd
    ? 'hover:bg-sky-200 hover:text-sky-900 focus-visible:outline-sky-600'
    : 'hover:bg-red-200 hover:text-red-900 focus-visible:outline-red-600';
  const buttonClass = `inline-flex items-center justify-center rounded-full leading-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 ${tone}`;

  return (
    <span
      title={title}
      className={
        isAdd
          ? 'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-dashed border-sky-400 bg-sky-50 text-sky-800'
          : 'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-red-300 bg-red-50 text-red-800'
      }
    >
      {!isAdd && <MinusIcon />}
      <span className={isAdd ? undefined : 'line-through'}>{suggestion.tag}</span>
      {isAdd && suggestion.is_new_tag ? (
        <ChipPill
          title="New vocabulary — accepting adds this tag to the inventory for future videos"
          className="bg-sky-100 text-sky-700"
        >
          new
        </ChipPill>
      ) : null}
      <button
        type="button"
        onClick={() => onDecide?.(suggestion, 'accept')}
        aria-label={
          isAdd
            ? `Accept suggestion to add ${suggestion.tag}`
            : `Accept suggestion to remove ${suggestion.tag}`
        }
        title={isAdd ? `Accept — add "${suggestion.tag}"` : `Accept — remove "${suggestion.tag}"`}
        className={`${buttonClass} w-4 h-4`}
      >
        <TickIcon />
      </button>
      <button
        type="button"
        onClick={() => onDecide?.(suggestion, 'reject')}
        aria-label={`Dismiss suggestion for ${suggestion.tag}`}
        title="Dismiss — records a rejection so the model learns"
        className={`${buttonClass} px-1 text-sm`}
      >
        ×
      </button>
    </span>
  );
}

export function TagEditor({
  tags,
  onChange,
  suggestions = [],
  acceptedTags = [],
  pendingSuggestions = [],
  onDecideSuggestion,
}: TagEditorProps) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listId = useId();

  const normalizedInput = useMemo(() => normalizeTag(input), [input]);

  const pendingAdds = useMemo(
    () => pendingSuggestions.filter((s) => s.action === 'add'),
    [pendingSuggestions],
  );
  const pendingRemoves = useMemo(
    () => pendingSuggestions.filter((s) => s.action === 'remove'),
    [pendingSuggestions],
  );
  // A removal proposal takes over its tag's chip: showing both the manual chip
  // and the red proposal would duplicate the tag. Dismissing the proposal puts
  // the manual chip back.
  const pendingRemoveLower = useMemo(
    () => new Set(pendingRemoves.map((s) => s.tag.toLowerCase())),
    [pendingRemoves],
  );
  const visibleTags = useMemo(
    () => tags.filter((t) => !pendingRemoveLower.has(t.toLowerCase())),
    [tags, pendingRemoveLower],
  );
  const sortedAcceptedTags = useMemo(
    () => [...acceptedTags].sort((a, b) => a.localeCompare(b)),
    [acceptedTags],
  );

  const filteredSuggestions = useMemo(() => {
    // Never offer a tag the video already has or one a pending addition
    // proposes — pending proposals must not double up with manual picks.
    const taken = new Set([
      ...tags.map((t) => t.toLowerCase()),
      ...acceptedTags.map((t) => t.toLowerCase()),
      ...pendingAdds.map((s) => s.tag.toLowerCase()),
    ]);
    const remaining = suggestions.filter((s) => !taken.has(s.toLowerCase()));
    if (!normalizedInput) return remaining.slice(0, 8);

    const query = normalizedInput.toLowerCase();
    const prefix: string[] = [];
    const interior: string[] = [];
    for (const s of remaining) {
      const lower = s.toLowerCase();
      if (lower.startsWith(query)) prefix.push(s);
      else if (lower.includes(query)) interior.push(s);
    }
    return [...prefix, ...interior].slice(0, 8);
  }, [suggestions, tags, acceptedTags, pendingAdds, normalizedInput]);

  // Close dropdown on click outside.
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const addTag = (raw: string) => {
    const tag = normalizeTag(raw);
    if (!tag) return;
    // Accepted tags are real video_tags rows — re-adding one would collide with
    // the UNIQUE (stem, tag) constraint and 500 the save, so dedupe against
    // them here exactly like the dropdown does.
    const taken = new Set([
      ...tags.map((t) => t.toLowerCase()),
      ...acceptedTags.map((t) => t.toLowerCase()),
    ]);
    if (taken.has(tag.toLowerCase())) {
      setInput('');
      setActiveIndex(null);
      setOpen(false);
      return;
    }
    onChange([...tags, tag].sort((a, b) => a.localeCompare(b)));
    setInput('');
    setActiveIndex(null);
    setOpen(false);
  };

  const removeTag = (tag: string) => {
    onChange(tags.filter(t => t !== tag));
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    setActiveIndex(null);
    if (!open) setOpen(true);
  };

  const handleFocus = () => {
    setOpen(true);
    setActiveIndex(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setActiveIndex(e.key === 'ArrowDown' ? 0 : filteredSuggestions.length - 1);
        return;
      }
      if (filteredSuggestions.length === 0) return;
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      const next = activeIndex == null
        ? (e.key === 'ArrowDown' ? 0 : filteredSuggestions.length - 1)
        : activeIndex + delta;
      if (next >= 0 && next < filteredSuggestions.length) {
        setActiveIndex(next);
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (
        activeIndex != null &&
        activeIndex >= 0 &&
        activeIndex < filteredSuggestions.length &&
        filteredSuggestions[activeIndex]
      ) {
        addTag(filteredSuggestions[activeIndex]);
      } else {
        addTag(input);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setActiveIndex(null);
      return;
    }

    if (e.key === 'Tab') {
      setOpen(false);
      setActiveIndex(null);
      return;
    }
  };

  const activeId = activeIndex != null ? `${listId}-option-${activeIndex}` : undefined;
  const noTags =
    visibleTags.length === 0 &&
    sortedAcceptedTags.length === 0 &&
    pendingSuggestions.length === 0;

  return (
    <div ref={containerRef} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 relative">
      <h2 className="text-base font-semibold text-gray-900 mb-3">Category tags</h2>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {visibleTags.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700"
          >
            {tag}
            <button
              onClick={() => removeTag(tag)}
              aria-label={`Remove ${tag}`}
              className="text-gray-400 hover:text-gray-700 leading-none"
            >
              ×
            </button>
          </span>
        ))}
        {sortedAcceptedTags.map(tag => (
          <AcceptedTagChip key={tag} tag={tag} />
        ))}
        {pendingAdds.map((s) => (
          <SuggestionChip key={`suggestion-${s.id}`} suggestion={s} onDecide={onDecideSuggestion} />
        ))}
        {pendingRemoves.map((s) => (
          <SuggestionChip key={`suggestion-${s.id}`} suggestion={s} onDecide={onDecideSuggestion} />
        ))}
        {noTags && <span className="text-xs text-gray-400">No tags yet</span>}
      </div>
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-expanded={open && filteredSuggestions.length > 0}
            aria-controls={`${listId}-listbox`}
            aria-autocomplete="list"
            aria-activedescendant={activeId}
            placeholder="Add a tag and press Enter"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
          {open && filteredSuggestions.length > 0 && (
            <ul
              id={`${listId}-listbox`}
              role="listbox"
              className="absolute z-10 left-0 right-0 mt-1 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg py-1"
            >
              {filteredSuggestions.map((suggestion, index) => (
                <li
                  key={suggestion}
                  id={`${listId}-option-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addTag(suggestion);
                    inputRef.current?.focus();
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`px-3 py-2 text-sm cursor-pointer truncate ${
                    index === activeIndex ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                  }`}
                >
                  {suggestion}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          onClick={() => addTag(input)}
          disabled={!normalizeTag(input)}
          className="text-sm px-3 py-2 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>
    </div>
  );
}
